import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const STATUS_LABELS = {
    draft:        { label: 'Черновик',       color: '#8c8c8c' },
    on_agreement: { label: 'Келісуде',        color: '#faad14' },
    signing:      { label: 'Қол қоюда',      color: '#1890ff' },
    completed:    { label: 'Аяқталды',        color: '#52c41a' },
    cancelled:    { label: 'Отозвать етілді', color: '#ff4d4f' },
};

const ContractDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [contract, setContract]         = useState(null);
    const [loading, setLoading]           = useState(true);
    const [signing, setSigning]           = useState(false);   // ✅ ЭЦП
    const [finalizing, setFinalizing]     = useState(false);
    const [revoking, setRevoking]         = useState(false);
    const [showFinalize, setShowFinalize] = useState(false);
    const [previewHtml, setPreviewHtml]   = useState(null);
    const [showModal, setShowModal]       = useState(false);
    const [previewing, setPreviewing]     = useState(false);

    // ✅ ЭЦП статус
    const [canSign, setCanSign]           = useState(false);
    const [isAlreadySigned, setIsAlreadySigned] = useState(false);

    const [finalizeForm, setFinalizeForm] = useState({
        contract_number: '',
        contract_date:   '',
    });

    const currentUserId = parseInt(localStorage.getItem('user_id'));

    const config = {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    };

    const fetchContract = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `https://expertise.gov.kz/api/contracts/${id}`,
                config
            );
            const data = res.data;
            setContract(data);

            // ✅ ЭЦП логикасы — ордердегідей
            if (data.signatures) {
                const mySig = data.signatures.find(
                    s => parseInt(s.user_id) === currentUserId
                );
                if (mySig) {
                    const alreadySigned = mySig.is_signed === 1 || mySig.is_signed === true;
                    setIsAlreadySigned(alreadySigned);

                    const previousOnes = data.signatures.filter(
                        s => s.signing_order < mySig.signing_order
                    );
                    const allPreviousSigned = previousOnes.every(
                        s => s.is_signed === 1 || s.is_signed === true
                    );

                    if (data.status === 'signing' && !alreadySigned && allPreviousSigned) {
                        setCanSign(true);
                    } else {
                        setCanSign(false);
                    }
                }
            }

        } catch (err) {
            alert('Келісімшартты жүктеу қатесі!');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContract();
    }, [id]);

    // ✅ ЭЦП қол қою — ордердегідей
    const handleSignDocument = async () => {
        if (!window.NCA) {
            return alert('ncalayer-utils.js жүктелмеген. Бетті жаңартып көріңіз.');
        }
        setSigning(true);
        try {
            const isConnected = await window.NCA.connect();
            if (!isConnected) {
                alert('NCALayer іске қосылмаған. Бағдарламаның қосулы екенін тексеріңіз.');
                setSigning(false);
                return;
            }

            // 1. PDF-ті Base64 форматта алу
            const resBase64 = await axios.get(
                `https://expertise.gov.kz/api/contracts/${id}/pdf-base64`,
                config
            );
            const base64ToSign = resBase64.data.base64;

            // 2. NCALayer арқылы қол қою
            window.NCA.createCMSSignatureFromBase64(
                'PKCS12',
                'SIGNATURE',
                base64ToSign,
                true,
                async res => {
                    if (res.getCode() === '200') {
                        const signedCMS = res.getResponseObject();
                        try {
                            await axios.post(
                                `https://expertise.gov.kz/api/contracts/${id}/sign`,
                                { signed_pdf_base64: signedCMS },
                                config
                            );
                            alert('Құжатқа сәтті қол қойылды!');
                            fetchContract(); // жаңарту
                        } catch (e) {
                            alert(
                                'Тексеру қатесі: ' +
                                (e.response?.data?.message || 'ЭЦП жарамсыз')
                            );
                        }
                    } else {
                        if (res.getCode() !== '100') {
                            alert('Қате: ' + res.getMessage());
                        }
                    }
                    setSigning(false);
                }
            );
        } catch (err) {
            alert('Жүйелік қате: ' + err.message);
            setSigning(false);
        }
    };

    const handlePreview = async () => {
        setPreviewing(true);
        try {
            const res = await axios.post(
                'https://expertise.gov.kz/api/contracts/preview',
                { contract_data: contract.contract_data },
                config
            );
            setPreviewHtml(res.data.html);
            setShowModal(true);
        } catch (err) {
            alert('Предпросмотр қатесі: ' + (err.response?.data?.error || err.message));
        } finally {
            setPreviewing(false);
        }
    };

    const handleFinalize = async () => {
        if (!finalizeForm.contract_number || !finalizeForm.contract_date) {
            alert('Номер мен күнді толтырыңыз!');
            return;
        }
        setFinalizing(true);
        try {
            const res = await axios.post(
                `https://expertise.gov.kz/api/contracts/${id}/finalize`,
                finalizeForm,
                config
            );
            alert(res.data.message);
            setShowFinalize(false);
            fetchContract();
        } catch (err) {
            alert(err.response?.data?.message || err.response?.data?.error || 'Қате!');
        } finally {
            setFinalizing(false);
        }
    };

    const handleRevoke = async () => {
        if (!window.confirm('Келісімшартты отозвать етесіз бе?')) return;
        setRevoking(true);
        try {
            await axios.post(
                `https://expertise.gov.kz/api/contracts/${id}/revoke`,
                {},
                config
            );
            alert('Отозвать етілді');
            fetchContract();
        } catch (err) {
            alert(err.response?.data?.message || 'Қате!');
        } finally {
            setRevoking(false);
        }
    };

    if (loading) return <div style={styles.center}>Жүктелуде...</div>;
    if (!contract) return <div style={styles.center}>Келісімшарт табылмады</div>;

    const statusInfo   = STATUS_LABELS[contract.status] || { label: contract.status, color: '#000' };
    const contractData = contract.contract_data || {};
    const expertInfo   = contractData.expert_info || {};
    const items        = contractData.items_table || [];
    const totals       = contractData.totals || {};
    const dates        = contractData.dates || {};

    const isExecutor = contract.signatures?.some(
        s => parseInt(s.user_id) === currentUserId && s.role === 'executor'
    );
    const isCreator = contract.creator_id === currentUserId;

    return (
        <>
            <div style={styles.container}>
                <div style={styles.card}>

                    {/* ===== HEADER ===== */}
                    <div style={styles.header}>
                        <button onClick={() => navigate(-1)} style={styles.btnBack}>
                            Артқа
                        </button>
                        <div style={styles.headerCenter}>
                            <h2 style={styles.title}>
                                {contract.contract_number
                                    ? `№ ${contract.contract_number}`
                                    : 'Нөмір қойылмаған'}
                            </h2>
                            <span style={{ ...styles.statusBadge, backgroundColor: statusInfo.color }}>
                                {statusInfo.label}
                            </span>
                        </div>
                        <div style={{width: '80px'}} />
                    </div>

                    {/* ===== НЕГІЗГІ МӘЛІМЕТТЕР ===== */}
                    <div style={styles.section}>
                        <h4 style={styles.sectionTitle}>Негізгі мәліметтер</h4>
                        <div style={styles.infoGrid}>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>Келісімшарт нөмірі:</span>
                                <span style={styles.infoValue}>{contract.contract_number || '—'}</span>
                            </div>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>Күні:</span>
                                <span style={styles.infoValue}>{contract.contract_date || '—'}</span>
                            </div>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>Жасаушы:</span>
                                <span style={styles.infoValue}>{contract.creator?.name || '—'}</span>
                            </div>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>Мерзімі:</span>
                                <span style={styles.infoValue}>{dates.start} — {dates.end}</span>
                            </div>
                        </div>
                    </div>

                    {/* ===== САРАПШЫ МӘЛІМЕТТЕРІ ===== */}
                    <div style={styles.section}>
                        <h4 style={styles.sectionTitle}>Сарапшы мәліметтері</h4>
                        <div style={styles.infoGrid}>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>ФИО:</span>
                                <span style={styles.infoValue}>{expertInfo.fio || '—'}</span>
                            </div>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>Лауазым:</span>
                                <span style={styles.infoValue}>{expertInfo.position || '—'}</span>
                            </div>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>ИИН:</span>
                                <span style={styles.infoValue}>{expertInfo.iin || '—'}</span>
                            </div>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>IIK:</span>
                                <span style={styles.infoValue}>{expertInfo.bank_account || '—'}</span>
                            </div>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>Банк:</span>
                                <span style={styles.infoValue}>{expertInfo.bank_name || '—'}</span>
                            </div>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>Телефон:</span>
                                <span style={styles.infoValue}>{expertInfo.phone || '—'}</span>
                            </div>
                        </div>
                    </div>

                    {/* ===== ЖҰМЫС КЕСТЕСІ ===== */}
                    <div style={styles.section}>
                        <h4 style={styles.sectionTitle}>Жұмыс көлемі</h4>
                        <div style={{overflowX: 'auto'}}>
                            <table style={styles.table}>
                                <thead>
                                    <tr style={{backgroundColor: '#f0f5ff'}}>
                                        <th style={styles.th}>№</th>
                                        <th style={styles.th}>Атауы</th>
                                        <th style={styles.th}>Сынып</th>
                                        <th style={styles.th}>Бет</th>
                                        <th style={styles.th}>Б.табақ</th>
                                        <th style={styles.th}>Минут</th>
                                        <th style={styles.th}>Бағасы</th>
                                        <th style={styles.th}>Жиыны</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, i) => (
                                        <tr key={i}>
                                            <td style={styles.td}>{i + 1}</td>
                                            <td style={{...styles.td, textAlign: 'left'}}>{item.title}</td>
                                            <td style={styles.td}>{item.grade}</td>
                                            <td style={styles.td}>{item.page_count}</td>
                                            <td style={styles.td}>{item.baspa_tabak}</td>
                                            <td style={styles.td}>{item.duration}</td>
                                            <td style={styles.td}>{item.unit_price} ₸</td>
                                            <td style={{...styles.td, fontWeight: 'bold', color: '#2b5da7'}}>
                                                {item.total_row} ₸
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{backgroundColor: '#f6ffed', fontWeight: 'bold'}}>
                                        <td colSpan={7} style={{...styles.td, textAlign: 'right'}}>Жалпы сома:</td>
                                        <td style={{...styles.td, color: '#389e0d'}}>
                                            {totals.amount_numeric} ₸
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {/* ===== WORKFLOW / ҚОЛ ҚОЮШЫЛАР ===== */}
                    <div style={styles.section}>
                        <h4 style={styles.sectionTitle}>Қол қоюшылар</h4>
                        {contract.signatures?.map((sig, i) => (
                            <div key={i} style={styles.sigRow}>
                                <div style={styles.sigOrder}>{sig.signing_order}</div>
                                <div style={styles.sigInfo}>
                                    <div style={styles.sigName}>{sig.user?.name || '—'}</div>
                                    <div style={styles.sigRole}>{sig.role_name} • {sig.role}</div>
                                </div>
                                <div style={{
                                    ...styles.sigStatus,
                                    backgroundColor: sig.is_signed ? '#f6ffed' : '#fff7e6',
                                    color:           sig.is_signed ? '#52c41a' : '#faad14',
                                    border: `1px solid ${sig.is_signed ? '#b7eb8f' : '#ffd591'}`,
                                }}>
                                    {sig.is_signed
                                        ? `Қол қойылды ${sig.signed_at ? new Date(sig.signed_at).toLocaleDateString('ru-RU') : ''}`
                                        : 'Күтілуде'}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ===== PDF СКАЧАТЬ ===== */}
                    {contract.pdf_path && (
                        <div style={styles.section}>
                            <h4 style={styles.sectionTitle}>PDF құжат</h4>
                            
                               <a href={`https://expertise.gov.kz/storage/${contract.pdf_path}`}
                                target="_blank"
                                rel="noreferrer"
                                style={styles.btnDownload}
                            >
                                PDF жүктеу
                            </a>
                        </div>
                    )}

                    {/* ===== ӘРЕКЕТТЕР ===== */}
                    <div style={styles.actions}>
                        <button
                            onClick={handlePreview}
                            disabled={previewing}
                            style={styles.btnPreview}
                        >
                            {previewing ? 'Жүктелуде...' : 'Предпросмотр'}
                        </button>

                        {isExecutor && contract.status === 'on_agreement' && (
                            <button
                                onClick={() => setShowFinalize(!showFinalize)}
                                style={styles.btnFinalize}
                            >
                                Номер қою
                            </button>
                        )}

                        {isCreator && ['on_agreement', 'signing'].includes(contract.status) && (
                            <button
                                onClick={handleRevoke}
                                disabled={revoking}
                                style={styles.btnRevoke}
                            >
                                {revoking ? 'Өңделуде...' : 'Отозвать'}
                            </button>
                        )}
                    </div>

                    {/* ===== НОМЕР ҚОЮ ФОРМАСЫ ===== */}
                    {showFinalize && (
                        <div style={styles.finalizeBox}>
                            <h4 style={{margin: '0 0 15px'}}>Келісімшартты тіркеу</h4>
                            <div style={styles.finalizeGrid}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Келісімшарт нөмірі: *</label>
                                    <input
                                        style={styles.input}
                                        placeholder="мысалы: 2025-001"
                                        value={finalizeForm.contract_number}
                                        onChange={(e) => setFinalizeForm(prev => ({
                                            ...prev, contract_number: e.target.value
                                        }))}
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Келісімшарт күні: *</label>
                                    <input
                                        type="date"
                                        style={styles.input}
                                        value={finalizeForm.contract_date}
                                        onChange={(e) => setFinalizeForm(prev => ({
                                            ...prev, contract_date: e.target.value
                                        }))}
                                    />
                                </div>
                            </div>
                            <div style={{display: 'flex', gap: '10px', marginTop: '15px'}}>
                                <button
                                    onClick={handleFinalize}
                                    disabled={finalizing}
                                    style={styles.btnSubmit}
                                >
                                    {finalizing ? 'PDF жасалуда...' : 'Тіркеу және PDF жасау'}
                                </button>
                                <button
                                    onClick={() => setShowFinalize(false)}
                                    style={styles.btnCancel}
                                >
                                    Болдырмау
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ===== ЭЦП ҚОЛ ҚОЮ БЛОГЫ ===== */}
                    <div style={{marginTop: '20px'}}>

                        {/* Кезегі келді — қол қою */}
                        {contract.status === 'signing' && canSign && (
                            <div style={styles.signingAlert}>
                                <p style={styles.signingHint}>
                                    Сіздің кезегіңіз келді! ЭЦП арқылы қол қоюыңызды сұраймыз.
                                </p>
                                <button
                                    style={styles.btnSign}
                                    onClick={handleSignDocument}
                                    disabled={signing}
                                >
                                    {signing ? 'Күтіңіз...' : 'ЭЦП-мен қол қою'}
                                </button>
                            </div>
                        )}

                        {/* Бұрын қол қойылған */}
                        {contract.status === 'signing' && isAlreadySigned && (
                            <div style={styles.alertSuccess}>
                                Сіз бұл келісімшартқа қол қойып қойғансыз.
                            </div>
                        )}

                        {/* Кезегін күту */}
                        {contract.status === 'signing' && !canSign && !isAlreadySigned && (
                            <div style={styles.alertInfo}>
                                Кезегіңізді күтіңіз. Алдыңғы қатысушылар қол қоюы керек.
                            </div>
                        )}

                        {/* on_agreement — executor емес */}
                        {contract.status === 'on_agreement' && !isExecutor && (
                            <div style={styles.alertInfo}>
                                Келісімшарт орындаушының тіркеуін күтіп тұр...
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* ===== ПРЕДПРОСМОТР МОДАЛЫ ===== */}
            {showModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <div style={styles.modalHeader}>
                            <h3 style={{margin: 0}}>Келісімшарттың алдын ала нұсқасы</h3>
                            <button onClick={() => setShowModal(false)} style={styles.btnClose}>
                                Жабу
                            </button>
                        </div>
                        <iframe
                            style={{flex: 1, border: 'none', width: '100%', backgroundColor: '#fff'}}
                            srcDoc={previewHtml}
                            title="Предпросмотр"
                        />
                    </div>
                </div>
            )}
        </>
    );
};

const styles = {
    container:    { padding: '20px', backgroundColor: '#f0f2f5', minHeight: '100vh' },
    card:         { backgroundColor: '#fff', padding: '30px', borderRadius: '8px', maxWidth: '1000px', margin: '0 auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
    center:       { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontSize: '18px' },
    header:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '25px', borderBottom: '2px solid #2b5da7', paddingBottom: '15px' },
    headerCenter: { textAlign: 'center' },
    title:        { margin: '0 0 8px', color: '#2b5da7', fontSize: '22px' },
    statusBadge:  { color: '#fff', padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold' },
    btnBack:      { backgroundColor: '#f0f0f0', border: '1px solid #d9d9d9', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
    section:      { marginBottom: '25px', padding: '20px', border: '1px solid #e8e8e8', borderRadius: '8px', backgroundColor: '#fafafa' },
    sectionTitle: { margin: '0 0 15px', color: '#333', fontSize: '15px', fontWeight: '600' },
    infoGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' },
    infoRow:      { display: 'flex', flexDirection: 'column', gap: '3px' },
    infoLabel:    { fontSize: '12px', color: '#888' },
    infoValue:    { fontSize: '14px', color: '#333', fontWeight: '500' },
    table:        { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th:           { border: '1px solid #d9d9d9', padding: '8px', textAlign: 'center', fontWeight: '600', fontSize: '12px' },
    td:           { border: '1px solid #d9d9d9', padding: '7px', textAlign: 'center' },
    sigRow:       { display: 'flex', alignItems: 'center', gap: '15px', padding: '12px', border: '1px solid #e8e8e8', borderRadius: '6px', marginBottom: '8px', backgroundColor: '#fff' },
    sigOrder:     { width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#2b5da7', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 },
    sigInfo:      { flex: 1 },
    sigName:      { fontWeight: '600', fontSize: '14px' },
    sigRole:      { fontSize: '12px', color: '#888', marginTop: '2px' },
    sigStatus:    { padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', flexShrink: 0 },
    actions:      { display: 'flex', gap: '12px', marginTop: '10px', flexWrap: 'wrap' },
    btnPreview:   { backgroundColor: '#1890ff', color: '#fff', padding: '10px 24px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
    btnFinalize:  { backgroundColor: '#2b5da7', color: '#fff', padding: '10px 24px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
    btnRevoke:    { backgroundColor: '#ff4d4f', color: '#fff', padding: '10px 24px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
    btnDownload:  { display: 'inline-block', backgroundColor: '#52c41a', color: '#fff', padding: '10px 24px', borderRadius: '4px', textDecoration: 'none', fontWeight: 'bold', fontSize: '14px' },
    finalizeBox:  { marginTop: '20px', padding: '20px', border: '2px solid #2b5da7', borderRadius: '8px', backgroundColor: '#f0f5ff' },
    finalizeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
    formGroup:    { display: 'flex', flexDirection: 'column' },
    label:        { fontSize: '13px', marginBottom: '5px', color: '#555' },
    input:        { padding: '8px', borderRadius: '4px', border: '1px solid #d9d9d9', fontSize: '14px' },
    btnSubmit:    { backgroundColor: '#52c41a', color: '#fff', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' },
    btnCancel:    { backgroundColor: '#f0f0f0', color: '#333', padding: '10px 20px', border: '1px solid #d9d9d9', borderRadius: '4px', cursor: 'pointer' },

    // ✅ ЭЦП стилдері
    signingAlert: { backgroundColor: '#e6f7ff', border: '2px solid #1890ff', borderRadius: '8px', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '15px' },
    signingHint:  { margin: 0, color: '#0050b3', fontWeight: '600', fontSize: '14px' },
    btnSign:      { backgroundColor: '#52c41a', color: '#fff', padding: '12px 28px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', whiteSpace: 'nowrap' },
    alertSuccess: { backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', padding: '15px 20px', color: '#389e0d', fontWeight: '600', fontSize: '14px' },
    alertInfo:    { backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: '8px', padding: '15px 20px', color: '#ad6800', fontWeight: '500', fontSize: '14px' },

    modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: '#fff', width: '85%', height: '90%', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    modalHeader:  { padding: '15px 20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    btnClose:     { backgroundColor: '#ff4d4f', color: '#fff', border: 'none', padding: '5px 15px', borderRadius: '4px', cursor: 'pointer' },
};

export default ContractDetail;